import { AsyncPipe } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
import { faCaretDown, faSearch } from '@fortawesome/pro-solid-svg-icons';
import {
  AaNgModule,
  DisplayStrategy,
  ISearchQuery,
  ITabDefinition,
  SearchType,
} from 'aa-ng';
import { BehaviorSubject, tap } from 'rxjs';

import { IInstitutionContext } from '../../../models';
import { DashboardService, InstitutionsService } from '../../services';
import { BaseDirective } from '../base.component';
import { HelpTextModalComponent } from '../help-text-modal/help-text-modal.component';

@Component({
  selector: 'app-dashboard-search',
  imports: [
    AaNgModule,
    AsyncPipe,
    FaIconComponent,
    FormsModule,
    HelpTextModalComponent,
  ],
  templateUrl: './dashboard-search.component.html',
  styleUrls: ['./dashboard-search.component.scss'],
})
export class DashboardSearchComponent extends BaseDirective {
  private institutionsService = inject(InstitutionsService);
  public dashboardService = inject(DashboardService);

  faQuestionCircle = faQuestionCircle;
  faCaretDown = faCaretDown;
  faSearch = faSearch;
  searchType = SearchType;
  helpTextTitle: string;
  helpTextContent: string;
  context: IInstitutionContext;
  _includedInstitutionIds: number[] = null;
  _allUniversitiesOptionInstitutionIds: number[] = null;
  selectedSearchType$ = new BehaviorSubject<SearchType>(SearchType.Topic);
  showDropdown: boolean = undefined;
  _open = false;
  searchLabel: string = 'Search Scholars';
  tabDefinitions: ITabDefinition[];
  currentTab: ITabDefinition;

  @ViewChild('input', { static: true })
  inputElement: ElementRef<HTMLInputElement>;
  @Input() set query(query: ISearchQuery) {
    if (query && !isNaN(query.type)) {
      this.selectedSearchType$.next(query.type);
      this.inputElement.nativeElement.value = query.query;
    }
  }

  @Output() searchInitiated = new EventEmitter<ISearchQuery>();
  @Output() rawtext = new EventEmitter<string>();

  _text: string;
  get text() {
    return this._text;
  }

  set text(value: string) {
    this._text = value;
    this.rawtext.emit(value);
  }

  constructor() {
    super();
    this._subscriptions.push(
      this.institutionsService.context.subscribe((context) => {
        this.context = context;
        if (context.institutionGroup) {
          this._allUniversitiesOptionInstitutionIds = Array.from(
            context.institutionGroup.institutions.map((i) => i.id),
          );
        }
        if (context.searchLabelText != null && context.searchLabelText !== '') {
          this.searchLabel = context.searchLabelText;
        }
      }),
    );

    this.tabDefinitions = [
      {
        displayStrategy: new DisplayStrategy('Research Topics'),
        data: SearchType.Topic,
        isCurrent: this.selectedSearchType$.value === SearchType.Topic,
        dataQaTag: 'search-type-tab-topic',
      },
      {
        displayStrategy: new DisplayStrategy('Scholar Name'),
        data: SearchType.Name,
        isCurrent: this.selectedSearchType$.value === SearchType.Name,
        dataQaTag: 'search-type-tab-name',
      },
    ];

    this._subscriptions.push(
      this.selectedSearchType$
        .pipe(
          tap((searchType) => {
            this.tabDefinitions.forEach(
              (tab) => (tab.isCurrent = tab.data === searchType),
            );
            this.currentTab = this.tabDefinitions.find(
              (tab) => tab.data === searchType,
            );
          }),
        )
        .subscribe(),
    );
  }

  requestSearch(input: string) {
    if (input) {
      this.searchInitiated.emit({
        query: input,
        type: this.selectedSearchType$.value,
        includedInstitutionIds:
          this._includedInstitutionIds ?
            this._includedInstitutionIds
          : this._allUniversitiesOptionInstitutionIds,
      });
    }
  }

  toggleSearchType(searchType: SearchType) {
    this.selectedSearchType$.next(searchType);
    this._open = !this._open;
  }

  toggleDD(open: boolean) {
    this._open = open;
  }

  changeView(tab: ITabDefinition) {
    this.selectedSearchType$.next(tab.data);
    this.currentTab = tab;
  }
}
